/**
 * onboarding_checklist_controller.js — the getting-started checklist (Stimulus).
 *
 * Attached to the checklist the onboard_checklist macro draws
 * (templates/macros/onboarding.html): on the dashboard, and inside the
 * navigation ring's menu once dismissed. Rules: docs/ONBOARDING_DESIGN_RULES.md
 * F1 to F3, M4.
 *
 * - Dismissing is the card's form: a POST to dashboard.dismiss_getting_started,
 *   which records it for the person and answers a Turbo Stream that draws the
 *   navigation ring. leave() plays the card's exit as the form goes; left()
 *   takes the card off the page once both the exit and the answer are in, or
 *   brings it back if the server refused. Dismissals made on a device before
 *   the server held them (a localStorage key) are sent once on connect, so a
 *   card someone already put away stays away.
 * - A tick done since this device last showed the checklist draws in on
 *   connect (M4); the ring does the same through onboard_ring_controller.js.
 * - opened() reports which item's action was tapped
 *   (checklist_item_opened, through _onboarding_beacon.js); the server works
 *   out the ticks itself.
 * - openForm() opens an item's create form in place on a page that carries
 *   it (forms_controller.js's window.showForm, the dashboard's
 *   #quoteFormContainer); anywhere else the item's link goes to the list page
 *   that has one.
 * - askChip() opens the Chip panel (components/ai_chat_widget.html listens).
 * - The "Confirm your email" action calls resendVerification(), which sends
 *   the request every confirm-link button shares (_resend_link.js): the
 *   server mails the business owner's address from the session, never one
 *   taken from the page.
 */
import { Controller } from "@hotwired/stimulus";
import { animationsFinished } from "./_onboard.js";
import { reportProgress } from "./_onboarding_beacon.js";
import { resendConfirmLink } from "./_resend_link.js";

export default class extends Controller {
    static targets = ["tick"];
    static values = { done: Array, business: String };

    connect() {
        if (this._sendEarlierDismissal()) return;
        this._drawNewTicks();
    }

    leave() {
        this.element.setAttribute("data-leaving", "");
    }

    async left(event) {
        if (!event.detail.success) {
            this.element.removeAttribute("data-leaving");
            showErrorMessage("Could not put the checklist away. Please try again.");
            return;
        }
        await animationsFinished(this.element);
        this.element.remove();
    }

    opened(event) {
        reportProgress("checklist_item_opened", { item: event.params.item });
    }

    openForm(event) {
        const { form } = event.params;
        if (!document.getElementById(`${form}FormContainer`) || typeof window.showForm !== "function") return;
        event.preventDefault();
        window.showForm(form);
    }

    askChip() {
        document.dispatchEvent(new CustomEvent("chippie:open-chip"));
    }

    async resendVerification(event) {
        const button = event.currentTarget;
        const originalContent = button.innerHTML;
        button.disabled = true;
        button.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Sending...';

        const { ok, message } = await resendConfirmLink();
        if (ok) {
            // Stays disabled: the link is on its way, and a second tap would
            // only send the same link again.
            button.textContent = 'Link sent, check your inbox';
            showSuccessMessage(message);
        } else {
            button.disabled = false;
            button.innerHTML = originalContent;
            showErrorMessage(message);
        }
    }

    /** Sends a dismissal this device recorded locally; true when it did. */
    _sendEarlierDismissal() {
        const form = this.element.querySelector("form.onboard-checklist__dismiss");
        const key = `chippie_onboarding_checklist_dismissed_${this.businessValue || "default"}`;
        try {
            if (!form || localStorage.getItem(key) !== "1") return false;
            localStorage.removeItem(key);
        } catch {
            return false;
        }
        form.requestSubmit();
        return true;
    }

    /** Draws in the ticks for items done since this device last showed them. */
    _drawNewTicks() {
        const key = `chippie_getting_started_done_${this.businessValue}`;
        let seen = null;
        try {
            seen = JSON.parse(localStorage.getItem(key) || "null");
            localStorage.setItem(key, JSON.stringify(this.doneValue));
        } catch {
            return;
        }
        if (!Array.isArray(seen)) return;
        const fresh = this.tickTargets.filter((tick) => !seen.includes(tick.closest("[data-item]")?.dataset.item));
        if (!fresh.length) return;
        fresh.forEach((tick) => tick.removeAttribute("data-done"));
        void this.element.getBoundingClientRect();
        fresh.forEach((tick) => tick.setAttribute("data-done", ""));
    }
}
